import { createMessageProxyClientToIFrame, MessageProxyClient } from '@fullstory/channel';
import { Lazy } from '@fullstory/channel/fireandforget';
import { Dispatcher, ProxiedFunctionCall } from '@fullstory/channel/proxy';
import { PageModel, PagePlatform, pagePlatformByType } from '@fullstory/playback-model';
import {
  ObjectCollection,
  ObjectInfo,
  ObjectQuery,
  PlayResult,
  RendererClientStub,
  RendererEventType,
  RendererEventTypeMap,
  RendererMessageProxyKey,
  RendererModeOptions,
  SeekResult,
} from '@fullstory/renderer-shared';
import { Point } from '@fullstory/util/geometry';
import { from, skip, Subscription } from 'rxjs';
import { once } from './once';
import { RendererEventDispatcher, RendererEventListenerCallback } from './RendererEventDispatcher';
import {
  ObjectObserverCallback,
  RendererObjectObserverDispatcher,
} from './RendererObjectObserverDispatcher';

export interface LoadResult {
  type: PagePlatform;
  totalTimeMs: number;
}

export type DeprecatedRendererClient = MessageProxyClient<RendererClientStub>;

interface RendererClientArgs {
  iframe: HTMLIFrameElement;
  page: Readonly<PageModel>;
  url: URL;
}

export const createRendererClient = (args: RendererClientArgs): RendererClient => {
  const { iframe, page, url } = args;

  const client = new RendererClient({ iframe, page, url });

  // Return the renderer
  return client;
};

/**
 * A client for communicating with the renderer iframe asynchronously.
 * Streamlines and simplifies the process of setting up a MessageProxyClient,
 * sending and receiving async messages, and avoiding deadlocks. Additionally,
 * enforces a few nice-to-have constraints, such as ensuring the iframe
 * is loaded before attempting to play or seek.
 */
export class RendererClient {
  #eventsSubscription?: Subscription;
  #iframe: HTMLIFrameElement;
  #isSuspended: boolean = false;
  #objectObserverDispatcher = new RendererObjectObserverDispatcher();
  #page: Readonly<PageModel>;
  #rendererEventDispatcher = new RendererEventDispatcher();
  #url: URL;

  #rejectPendingPlay = (_error?: unknown) => {};
  #rejectPendingSeek = (_error?: unknown) => {};

  constructor({ iframe, page, url }: RendererClientArgs) {
    this.#iframe = iframe;
    this.#page = page;
    this.#url = url;
    this.#rendererEventDispatcher.addEventListener(
      'observation',
      this.#objectObserverDispatcher.onObservation,
    );
  }

  get isSuspended(): boolean {
    return this.#isSuspended;
  }

  /**
   * @deprecated
   * Returns the full RendererClient, including deprecated methods.
   * Note: this method should only be used in legacy code that must
   * interact with the client imperatively (e.g. heatmaps, inspectmode, etc.).
   * Future code should use only the methods exposed on this class directly.
   * If a method seems to be missing, it's probably because a declarative version
   * needs to be implemented in the Renderer React component itself.
   */
  async getDeprecatedClient(): Promise<DeprecatedRendererClient> {
    return this._getClient();
  }

  /**
   * Loads the page iframe, sets up the client, and kicks off resource preloading.
   * Note: the resource preloading is difficult to spot at first glance, but
   * it actually occurs when the Renderer constructor initiates stylsheet
   * resource preloading immediately upon invocation. The Renderer constructor
   * is invoked when the iframe is created, which ultimately happens when
   * this method is called.
   * NOTE: this method will ignore subsequent calls.
   */
  async load(): Promise<LoadResult> {
    const { result } = await this._loadOnce();
    return result;
  }

  /**
   * Plays the page to an initial timestamp after ensuring the iframe and client are loaded.
   * If the method has already been called once, it will call seek instead.
   */
  async play(options: { offsetMs: number }): Promise<PlayResult> {
    return new Promise((resolve, reject) => {
      this.#rejectPendingPlay = reject;
      this._play(options).then(resolve).catch(reject);
    });
  }

  /**
   * Resume any suspended processes.
   */
  async resume(): Promise<void> {
    this.#isSuspended = false;
  }

  /**
   * Seek to a specific offset in the page.
   * Note: this operation will ensure the iframe is loaded and the page is playing
   * before attempting to seek.
   */
  seek(offsetMs: number = 0): Promise<SeekResult> {
    return new Promise((resolve, reject) => {
      this.#rejectPendingSeek = reject;
      this._seek(offsetMs).then(resolve).catch(reject);
    });
  }

  /**
   * Suspend any CPU instensive processes in the Renderer to conserve resources.
   *
   * This method does not actually _pause_ playback per se,
   * since playback is controlled through {@link seek} calls. Instead,
   * it can be considered a performance optimization to help conserve
   * memory and CPU resources if, for example, multiple Renders are
   * being used simultaneously.
   */
  async suspend(): Promise<void> {
    this.#isSuspended = true;
    const client = await this._getClient();
    await client.onPause();
  }

  /**
   * @returns information about the rendered objects which match any of the given
   * patterns.
   *
   * If you need to know which patterns were matched by each object,
   * you can assign an identifier to each pattern using the 'group' property of
   * ObjectPattern and check for it in the 'groups' property of each entry in
   * the ObjectCollection.
   */
  async queryObjects(query: ObjectQuery): Promise<ObjectCollection> {
    const client = await this._getClient();
    return client.queryObjects(query);
  }

  /**
   * @returns information about the rendered objects which intersect the given
   * point. The results are ordered by depth; the first result is the topmost
   * object in the stack. The point is interpreted relative to the renderer
   * element; (0, 0) corresponds to the top left corner of the renderer.
   */
  async queryPoint(point: Point): Promise<ObjectInfo[]> {
    const client = await this._getClient();
    return client.queryPoint(point);
  }

  /**
   * Find all of the matching elements for a given selector.
   *
   * @deprecated Use queryObjects instead.
   */
  async querySelector(selector: string): Promise<ObjectInfo[]> {
    const client = await this._getClient();
    return client.querySelector(selector);
  }

  /** Set the renderer's mode. */
  async setMode(options: RendererModeOptions): Promise<void> {
    const client = await this._getClient();
    return client.setMode(options);
  }

  /**
   * Add a listener for renderer events.
   *
   * @returns a function that removes the listener when called, suitable for use
   * as a useEffect cleanup function.
   */
  addEventListener = <E extends RendererEventType>(
    event: E,
    listener: RendererEventListenerCallback<E>,
  ): (() => void) => {
    switch (event) {
      case 'observation':
        throw new Error('Use addObjectObserver instead');
      case 'resize':
        throw new Error('Use addResizeObserver instead');
    }

    this.#rendererEventDispatcher.addEventListener(event, listener);
    return () => this.#rendererEventDispatcher.removeEventListener(event, listener);
  };

  /**
   * Add an observer that continuously monitors the rendered objects which match
   * the given query.
   *
   * When addObjectObserver() is called, or when a possible change in any
   * observed object is detected thereafter, the provided callback will invoked.
   *
   * @returns a function that disconnects the observer, suitable for use as a
   * useEffect cleanup function.
   */
  addObjectObserver = (query: ObjectQuery, callback: ObjectObserverCallback): (() => void) => {
    let canceled = false;
    this.#objectObserverDispatcher.addObserver(callback, query);

    const startedObserving = (async () => {
      const client = await this._getClient();
      if (canceled) return;
      await client.observeObjects(this.#objectObserverDispatcher.query());
    })();

    return () => {
      canceled = true;
      callback({}); // clear the objects in the callback about to be removed
      this.#objectObserverDispatcher.removeObserver(callback);

      startedObserving.then(async () => {
        const client = await this._getClient();
        await client.observeObjects(this.#objectObserverDispatcher.query());
      });
    };
  };

  /**
   * Add an observer that continuously monitors the dimensions of the renderer.
   *
   * When addResizeObserver() is called, or when a possible change in the
   * renderer's dimensions is detected thereafter, the provided callback will
   * invoked.
   *
   * @returns a function that disconnects the observer, suitable for use as a
   * useEffect cleanup function.
   */
  addResizeObserver = (listener: (e: RendererEventTypeMap['resize']) => void): (() => void) => {
    let canceled = false;

    (async () => {
      const client = await this._getClient();
      if (canceled) return;
      const dimensions = await client.getDimensions();
      if (canceled) return;
      listener({ type: 'resize', dimensions });
      this.#rendererEventDispatcher.addEventListener('resize', listener);
    })();

    return () => {
      canceled = true;
      this.#rendererEventDispatcher.removeEventListener('resize', listener);
    };
  };

  /**
   * Cleanup any resources and subscriptions associated with the renderer.
   */
  async teardown(): Promise<void> {
    this.#eventsSubscription?.unsubscribe();
    this._playOnce.reset();
    this.#rejectPendingSeek('Canceled');
    this.#rejectPendingPlay('Canceled');

    const client = await this._getClient();

    return client.teardown();
  }

  private async _getClient(): Promise<DeprecatedRendererClient> {
    return this._createClientOnce();
  }

  /**
   * Initializes a message proxy client to the iframe, opens the page,
   * and returns the client. Returns the same client on subsequent calls.
   * This method cannot be reset.
   */
  private readonly _createClientOnce = once(async () => {
    const clientPromise = createMessageProxyClientToIFrame(
      this.#iframe,
      new RendererClientStub(),
      RendererMessageProxyKey,
    );

    const channel = Lazy<ProxiedFunctionCall>(this.#iframe);
    const dispatcher = new Dispatcher(channel);
    dispatcher.RegisterService(this.#rendererEventDispatcher, 'RendererHost');

    // NOTE: we must set the src attribute _after_ the client
    // has attached a listener to the iframe. We need to do this
    // to avoid a race condition where we might miss the load event
    // and fail to initalize the renderer (Safari 14 seems to have a
    // bug that makes it especially prone to this issue).
    this.#iframe.setAttribute('src', this.#url.href);

    // Open the page to kick off resource preloading, but not actual playback.
    const client = await clientPromise;
    await client.openPage(this.#page.getRawPageDesc());

    this.#eventsSubscription = from(this.#page.rawEvents.batches())
      .pipe(skip(1)) // The first batch isn't considered trailing
      .subscribe(next => {
        // sanity check
        if (next) {
          client?.appendTrailingEvents([...next]);
        }
      });

    return client;
  });

  /**
   * Initializes the iframe, client, and page.
   * Returns the client and the load result.
   * Returns the previou client and load result on subsequent calls.
   */
  private readonly _loadOnce = once(
    async (): Promise<{ client: DeprecatedRendererClient; result: LoadResult }> => {
      // Start tracking the time
      const start = performance.now();

      // Construct the client and capture the load result
      const client = await this._createClientOnce();
      const type = pagePlatformByType[this.#page.type];
      const totalTimeMs = performance.now() - start;
      const result = { type, totalTimeMs };

      return { client, result };
    },
  );

  private async _play(options: { offsetMs: number }): Promise<PlayResult> {
    const result = await this._playOnce(options);

    if (result && result.pageOffsetMs !== options.offsetMs) {
      await this.seek(options.offsetMs);
    }

    return result;
  }

  /**
   * Start playback for the page, seeking to initial offset.
   */
  private readonly _playOnce = once(async (options: { offsetMs: number }): Promise<PlayResult> => {
    const client = await this._getClient();

    const data = await this.#page.getData();

    const playOptions = { initialTimeStampMs: options?.offsetMs };

    return client.playPage(data, playOptions);
  });

  private async _seek(offsetMs: number = 0): Promise<SeekResult> {
    let wasSuspended = false;

    if (this.#isSuspended) {
      wasSuspended = true;
      this.resume();
    }
    // Throw an error if the offset is out of bounds
    //
    // NOTE: this logic _should_ account for boundaries between pages in a clip expr.
    // Previously, this edge case was handled by checking the current rangekind
    // for SKIP or GAP ranges in SessionPlayback.playTo().
    const duration = this.#page.duration ?? 0;
    if (offsetMs > duration) {
      throw new Error(
        `Tried to seek to an offset (${offsetMs}ms) that is out of page bounds (${duration}ms})`,
      );
    }

    const client = await this._getClient();

    // Play the page before seeking.
    // Note that this will be a noop if the page has already been played.
    await this._playOnce({ offsetMs });

    const result = await client.playToTime(offsetMs);

    if (wasSuspended) {
      this.suspend();
    }

    return result;
  }
}
